/*
 * Quick buy in a side drawer instead of the theme's centred modal.
 *
 * Works like the theme's <quick-buy-modal>: on open it fetches the product page and takes the
 * <template id="quick-buy-content"> that snippets/product-quick-buy.liquid renders there. That template now holds the
 * same blocks as the PDP (title, price, reviews, scents, quantity offers, add to cart, guarantee), so the drawer
 * mirrors the product page.
 *
 * Extras compared to the modal:
 * - the template's <script> tags are recreated so block scripts (quantity offers) also run on pages without them;
 * - clicking another scent (product variations) reloads the drawer with that product instead of leaving the page;
 * - swatches on a product card keep the drawer's product-url in step, like the theme does for the modal;
 * - the Casa loyalty banner gets its points, icon and styling from the app (its own script only fills the PDP's banner),
 *   after which assets/coco-coins.js swaps in the PDP sentence ("earn 2,500 Coco coins with this order").
 */

await customElements.whenDefined('x-drawer');

const Drawer = customElements.get('x-drawer');

if (!customElements.get('quick-buy-drawer')) {
  class QuickBuyDrawer extends Drawer {
    #loadedUrl = null;
    #listening = false;

    connectedCallback() {
      super.connectedCallback();

      if (this.#listening) {
        return;
      }

      this.#listening = true;
      this.addEventListener('click', this.#onClick.bind(this));
      this.addEventListener('dialog:after-hide', () => {
        this.replaceChildren();
        this.#loadedUrl = null;
      });

      // After adding to cart the cart drawer opens; close the quick buy so it does not stay open behind it
      document.addEventListener('cart:change', () => {
        if (this.hasAttribute('open')) {
          this.hide();
        }
      });
    }

    async show() {
      const promise = this.#load(this.getAttribute('product-url'));
      return super.show({ conditionToFulfill: promise });
    }

    async #load(url) {
      const showLoadingBar = this.controls.every((control) => control.querySelector('button-content') === null);
      [this, ...this.controls].forEach((control) => control.setAttribute('aria-busy', 'true'));

      if (showLoadingBar) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      }

      try {
        const response = await fetch(url);
        const tempDoc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const template = tempDoc.getElementById('quick-buy-content');

        if (template) {
          const fragment = template.content.cloneNode(true);

          // The Casa block's inline script sets page-wide globals (product_id, variants…) that the PDP's own banner
          // relies on; read what the drawer needs from it and leave it out
          let casaData = null;

          fragment.querySelectorAll('script').forEach((script) => {
            if (script.textContent.includes('casa_product_price')) {
              casaData = {
                price: script.textContent.match(/casa_product_price\s*=\s*`([^`]*)`/)?.[1],
                variantId: script.textContent.match(/selected_variant\s*=\s*`([^`]*)`/)?.[1],
                productId: script.textContent.match(/product_id\s*=\s*`([^`]*)`/)?.[1]
              };
              script.remove();
            }
          });

          // Scripts coming from DOMParser never run, so recreate them
          fragment.querySelectorAll('script').forEach((oldScript) => {
            const script = document.createElement('script');
            [...oldScript.attributes].forEach((attribute) => script.setAttribute(attribute.name, attribute.value));
            script.textContent = oldScript.textContent;
            oldScript.replaceWith(script);
          });

          this.replaceChildren(...fragment.children);
          this.#loadedUrl = url;

          if (casaData) {
            this.#loadCoins(casaData);
          }
        }
      } finally {
        [this, ...this.controls].forEach((control) => control.setAttribute('aria-busy', 'false'));

        if (showLoadingBar) {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      }
    }

    // Same request and the same banner settings as the app's casa_product.js does for the PDP banner
    async #loadCoins({ price, variantId, productId }) {
      const banner = this.querySelector('.casa_banner_product');
      const text = banner?.querySelector('.casa_points_text');

      if (!banner || !text) {
        return;
      }

      banner.style.display = 'none';

      try {
        const localePrefix = location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i)?.[0] || '';
        const customerId = window.ShopifyAnalytics?.meta?.page?.customerId ?? null;
        const response = await fetch(`${location.origin}${localePrefix}/apps/casa-loyalty/get-frontend-data`, {
          method: 'POST',
          body: JSON.stringify({
            type: 'product',
            product_price: price,
            product_id: productId,
            variant_id: variantId,
            shopLocale: window.Shopify?.locale || null,
            customerId
          })
        });
        const data = await response.json();
        const settings = data.bannerSettings || {};

        if (data.customerExcluded || !(data.points_earned_value > 0)) {
          return;
        }

        const template = text.dataset.template || text.textContent;
        text.textContent = data.translations?.earnPointsText || template.replace('__POINTS__', data.points_earned);

        if (settings.textColor) text.style.color = settings.textColor;
        if (settings.fontSize) text.style.fontSize = `${settings.fontSize}px`;

        const icon = banner.querySelector('.casa_widget_banner-icon, .casa_banner_icon');

        if (icon) {
          if (settings.showIcon === false) {
            icon.style.display = 'none';
          } else if (settings.iconUrl) {
            icon.src = settings.iconUrl;
          }
        }

        const link = banner.querySelector('.casa_widget_banner_link');

        if (link) {
          const showLearnMore = settings.showLearnMore ?? data.showLearnMore;

          if (link.dataset.casaShowLocked !== 'true' && showLearnMore === false) {
            link.style.display = 'none';
          } else if (link.dataset.casaUrlLocked !== 'true' && (settings.learnMoreUrl || data.learnMoreUrl)) {
            link.href = settings.learnMoreUrl || data.learnMoreUrl;
          }
        }

        if (settings.bgColor) banner.style.backgroundColor = settings.bgColor;
        if (settings.padding) banner.style.padding = settings.padding;
        if (settings.boxShadow) banner.style.boxShadow = settings.boxShadow;
        if (settings.borderRadius != null) banner.style.borderRadius = `${settings.borderRadius}px`;

        banner.style.display = 'block';
      } catch (error) {
        // No banner rather than "0 coins"
      }
    }

    // Another scent (product variations block) stays inside the drawer
    async #onClick(event) {
      const link = event.target.closest('.quick-buy-drawer .variant-picker a[href*="/products/"]');

      if (!link || event.metaKey || event.ctrlKey || event.shiftKey) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const url = new URL(link.href, location.origin);
      url.hash = '';

      if (url.pathname === new URL(this.#loadedUrl || '', location.origin).pathname) {
        return;
      }

      this.setAttribute('product-url', `${url.pathname}${url.search}`);
      await this.#load(this.getAttribute('product-url'));
      this.getShadowPartByName?.('body')?.scrollTo({ top: 0 });
    }
  }

  customElements.define('quick-buy-drawer', QuickBuyDrawer);

  // Product card swatches update the product the drawer opens (the theme only does this for <quick-buy-modal>)
  document.addEventListener('change', (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement) || !target.closest('product-card')) {
      return;
    }

    const drawer = target.closest('product-card').querySelector('quick-buy-drawer');

    if (!drawer) {
      return;
    }

    if (target.hasAttribute('data-product-url')) {
      drawer.setAttribute('product-url', target.getAttribute('data-product-url'));
    } else if (target.hasAttribute('data-variant-id')) {
      const url = drawer.getAttribute('product-url').split('?')[0];
      drawer.setAttribute('product-url', `${url}?variant=${target.getAttribute('data-variant-id')}`);
    }
  });
}
